import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useState } from "react"; // 👈 useState와 useEffect를 import 합니다.
import { usePathname, useRouter } from "next/navigation";
import { AppMenu } from "@/shared";
import { match } from "path-to-regexp";
import Image from "next/image";
import { useMutation, useQuery } from "@tanstack/react-query";
import { authInfo, authLogout } from "@/entities";

/**
 * 전체 화면을 덮는 모바일 메뉴 컴포넌트 (애니메이션 적용)
 * @param {object} props
 * @param {function} props.onClose
 */

type SubNavItemProps = {
  menu: {
    paths?: string[];
    title: string;
  };
  onClose: () => void;
};
function SubNavItem({ menu, onClose }: SubNavItemProps) {
  const router = useRouter();
  const currentPath = usePathname() || "";
  const isActive =
    menu.paths &&
    menu.paths.some((path: any) => {
      const urlMatch = match(path);
      return urlMatch(currentPath);
    });

  const onClick = () => {
    if (menu.paths && menu.paths[0]) {
      router.push(menu.paths[0]);
      onClose();
    }
  };
  return (
    <button
      onClick={onClick}
      className={`
            block 
            hover:text-[#31777A]
            hover:bg-light-10
            transition-colors duration-150
            py-3
            ${isActive ? "text-[#31777A]" : "text-lightGray-30"}
          `}
    >
      <div className="regular-text-16">{menu.title}</div>
    </button>
  );
}

type NavItemProps = {
  menu: {
    paths?: string[];
    title?: string;
    children?: Array<{
      path: string;
      title: string;
    }>;
  };
  onClose: () => void;
};
function NavItem({ menu, onClose }: NavItemProps) {
  const router = useRouter();
  const currentPath = usePathname() || "";
  const isActive =
    menu.paths &&
    menu.paths.some((path: any) => {
      const urlMatch = match(path);
      return urlMatch(currentPath);
    });

  const title = menu.title && (
    <p
      className={`semibold-text-18 ${
        isActive ? "text-[#31777A]" : "text-black-almostBlack"
      }`}
    >
      {menu.title}
    </p>
  );

  const onClick = () => {
    if (menu.paths && menu.paths[0]) {
      router.push(menu.paths[0]);
      onClose();
    }
  };

  return (
    <div
      className={`
        w-1/2
        relative 
        group 
        transition-colors duration-200
        px-1 md:px-3
      `}
    >
      <div
        className={`
          menu-link 
          flex items-center gap-2 py-3
        `}
      >
        <button onClick={onClick}>{title}</button>
      </div>
      {menu.children && (
        <div className="menu-submenu">
          {menu.children.map((submenu, i) => (
            <SubNavItem key={i} menu={submenu} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  );
}

interface MobileFullMenuProps {
  onClose: () => void;
}
export function MobileMenu({ onClose }: MobileFullMenuProps) {
  const router = useRouter();

  // 1. 애니메이션 상태 관리
  const [isAnimating, setIsAnimating] = useState(false);
  const ANIMATION_DURATION = 500;

  // 2. 컴포넌트 마운트 시 애니메이션 시작
  useEffect(() => {
    requestAnimationFrame(() => setIsAnimating(true));
  }, []);

  // 3. 닫기 버튼/메뉴 클릭 핸들러
  const handleClose = (e: any) => {
    if (e) e.preventDefault();
    setIsAnimating(false);
    setTimeout(onClose, ANIMATION_DURATION);
  };

  // 4. 애니메이션 클래스 조합
  const menuClasses = `
    fixed inset-0 z-50 bg-white overflow-y-auto
    
    transition-transform duration-${ANIMATION_DURATION} ease-in-out  
    
    ${isAnimating ? "translate-x-0" : "translate-x-full"}
  `;

  const onClick = () => {
    router.push("/app/main");
    onClose();
  };

  const logoutMutation = useMutation({
    mutationFn: authLogout,
    onSuccess: () => {
      router.push("/app/main");
      onClose();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const onLogout = () => {
    if (!confirm("로그아웃 하시겠습니까?")) return;
    if (logoutMutation.isPending) return;
    logoutMutation.mutate();
  };

  const onLogin = () => {
    router.push("/auth/login");
    onClose();
  };

  const { error, isLoading, data } = useQuery({
    queryKey: ["authInfo"],
    queryFn: authInfo,
  });

  if (isLoading) return <></>;
  if (error) return <></>;

  return (
    // 5. 수정된 클래스 적용
    <div className={menuClasses}>
      <div className="fixed top-0 z-[1020] left-0 right-0 bg-white  ransition-shadow duration-300 ease-linear h-15">
        <div className="flex flex-row justify-between items-center h-full px-5">
          <div className="flex flex-row">
            <div className="relative w-8 h-8 flex mr-4">
              <button onClick={onClick}>
                <Image
                  src="/assets/images/logo/wLogo.svg"
                  alt="Symbol Logo"
                  fill
                  style={{ objectFit: "contain" }}
                />
              </button>
            </div>
          </div>
          {/* 닫기 버튼 */}
          <div className="flex justify-end">
            {data?.result ? (
              <button
                onClick={onLogout}
                className="bg-[#2373E8] text-white px-4 rounded-full medium-text-15"
              >
                로그아웃
              </button>
            ) : (
              <button
                onClick={onLogin}
                className="bg-[#2373E8] text-white px-4 rounded-full medium-text-15"
              >
                로그인
              </button>
            )}
            <button
              onClick={handleClose}
              aria-label="Close Menu"
              className="cursor-pointer p-2 rounded-full group hover:bg-gray-100 transition-colors duration-200 w-10 h-10"
            >
              <FontAwesomeIcon
                icon={faTimes}
                className={`text-lg 
                  transition-transform duration-300 ease-in-out
                  group-hover:rotate-90
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* 메뉴 리스트 */}
      <div className="flex flex-wrap pt-20 px-5 space-y-4">
        {AppMenu.filter((item) => item.is_active).map((item, i) => (
          <NavItem key={i} menu={item as any} onClose={onClose} />
        ))}
      </div>
    </div>
  );
}
